import React from "react"
import { TITLE_DESIGN_SYSTEM_PAGE } from "../../../../utils/constants"
import SideBarLayout from "../../../layout/side-bar-layout"
import { Box, Paper, Stack, Typography } from "@mui/material"
import CodeViewer from "../../../components/CodeViewer/code-viewer"
import DataGridTable from "../../../components/Table/data-grid-table"

const TableDataGridPage = () => {
    const TITLE_PAGE = `${TITLE_DESIGN_SYSTEM_PAGE} | Table`
    const exampleColumns = [
        { field: "id", headerName: "ID", width: 90 },
        { field: "name", headerName: "Nome", flex: 1 },
        { field: "status", headerName: "Status", width: 140 },
    ]

    const exampleRows = [
        { id: 1, name: "Arthur Morgaan", status: "Ativo" },
        { id: 2, name: "Sam Bridges", status: "Pendente" },
        { id: 3, name: "Tommy Vercetti", status: "Inativo" },
        { id: 4, name: "Gordon Freeman", status: "Ativo" },
        { id: 5, name: "Solid Snake", status: "Ativo" },
    ]

    const codeBasicUsage = `import DataGridTable from "src/v2/components/DataGridTable/data-grid-table"

const columns = [
  { field: "id", headerName: "ID", width: 90 },
  { field: "name", headerName: "Nome", flex: 1 },
  { field: "status", headerName: "Status", width: 140 }
]

const rows = [
  { id: 1, name: "Cliente A", status: "Ativo" },
  { id: 2, name: "Cliente B", status: "Pendente" },
  { id: 3, name: "Cliente C", status: "Inativo" },
]

<DataGridTable
  columns={columns}
  rows={rows}
  pageSize={25}
  loading={false} // em caso de true será mostrado como skeleton o loading
  pointerCursor
  onRowClick={(params) => console.log(params.row)}
/>`

    const codePropsType = `type DataGridTableProps = {
  columns: GridColDef[]
  rows: any[]
  onRowClick?: (params: any) => void
  pagination?: number[]           // ex: [25, 50, 100]
  pageSize?: number               // tamanho inicial da página
  disableDeleteColumn?: boolean
  loading?: boolean
  resetKey?: string               // força reset de página/filtro
  sx?: SxProps                    // estilização adicional
  pointerCursor?: boolean         // ativa cursor pointer nas linhas
}`

    return (
        <SideBarLayout
            header
            title={TITLE_PAGE}
            headerTitle={TITLE_PAGE}
        >
            <Box sx={{ mt: -2 }}>
                <Stack spacing={3}>

                    {/* SEÇÃO: INTRO + EXEMPLO */}
                    <Paper>
                        <Box p={3}>
                            <Typography variant="h4" gutterBottom color="common.white">
                                {`Table (DataGridTable)`}
                            </Typography>

                            <Typography variant="body2" color="text.secondary">
                                {`O componente DataGridTable encapsula o MUI DataGrid com paginação customizada, textos em português e estilos de tabela alinhados ao nosso design system. Ele é indicado para listagens com ordenação, filtro e paginação.`}
                            </Typography>

                            <Box mt={3}>
                                <Typography variant="h6" gutterBottom color="common.white">
                                    {`Exemplo principal`}
                                </Typography>

                                <Typography variant="body2" color="text.secondary" paragraph>
                                    {`Tabela simples com colunas de ID, nome e status. As linhas são clicáveis quando pointerCursor está habilitado.`}
                                </Typography>

                                <Box mt={2}>
                                    <DataGridTable
                                        columns={exampleColumns}
                                        rows={exampleRows}
                                        pageSize={25}
                                        pointerCursor
                                        onRowClick={(params) => {
                                            console.log(params.row)
                                            // Exemplo de uso: ação ao clicar na linha
                                            // console.log("Linha selecionada:", params.row)
                                        }}
                                    />
                                </Box>
                            </Box>
                        </Box>
                    </Paper>

                    {/* SEÇÃO: USO BÁSICO */}
                    <Paper>
                        <Box p={3}>
                            <Typography variant="h6" gutterBottom color="common.white">
                                {`Uso básico`}
                            </Typography>

                            <Typography variant="body2" color="text.secondary" paragraph>
                                {`Abaixo um exemplo mínimo de configuração de colunas, linhas e uso do DataGridTable com paginação padrão e clique na linha.`}
                            </Typography>

                            <Paper variant="outlined">
                                <Box p={2}>
                                    <CodeViewer code={codeBasicUsage} language="jsx" />
                                </Box>
                            </Paper>
                        </Box>
                    </Paper>

                    <Paper>
                        <Box p={3}>
                            <Typography variant="h6" gutterBottom color="common.white">
                                {`Tabela com loading`}
                            </Typography>

                            <Typography variant="body2" color="text.secondary" paragraph>
                                {`Tabela com o loading ativo`}
                            </Typography>

                            <Paper>
                                <Box mt={2}>
                                    <DataGridTable
                                        columns={exampleColumns}
                                        rows={exampleRows}
                                        pageSize={25}
                                        loading={true}
                                        pointerCursor
                                        onRowClick={(params) => {
                                            console.log(params.row)
                                            // Exemplo de uso: ação ao clicar na linha
                                            // console.log("Linha selecionada:", params.row)
                                        }}
                                    />
                                </Box>
                            </Paper>
                        </Box>
                    </Paper>

                    <Paper>
                        <Box p={3}>
                            <Typography variant="h6" gutterBottom color="common.white">
                                {`Tabela vazia`}
                            </Typography>

                            <Typography variant="body2" color="text.secondary" paragraph>
                                {`Tabela sem nenhum registro`}
                            </Typography>

                            <Paper>
                                <Box mt={2}>
                                    <DataGridTable
                                        columns={exampleColumns}
                                        rows={[]}
                                        pageSize={25}
                                        pointerCursor
                                        onRowClick={(params) => {
                                            console.log(params.row)
                                            // Exemplo de uso: ação ao clicar na linha
                                            // console.log("Linha selecionada:", params.row)
                                        }}
                                    />
                                </Box>
                            </Paper>
                        </Box>
                    </Paper>

                    {/* SEÇÃO: PROPRIEDADES */}
                    <Paper>
                        <Box p={3}>
                            <Typography variant="h6" gutterBottom color="common.white">
                                {`Propriedades do componente`}
                            </Typography>

                            <Typography variant="body2" color="text.secondary" paragraph>
                                {`Principais propriedades expostas pelo DataGridTable. Propriedades adicionais são repassadas diretamente para o MUI DataGrid via spread (...props).`}
                            </Typography>

                            <Paper variant="outlined">
                                <Box p={2}>
                                    <Typography variant="subtitle2" gutterBottom color="common.white">
                                        {`Tipo das props (exemplo em TypeScript)`}
                                    </Typography>
                                    <CodeViewer code={codePropsType} language="tsx" />
                                </Box>
                            </Paper>
                        </Box>
                    </Paper>
                </Stack>
            </Box>
        </SideBarLayout>
    )
}

export default TableDataGridPage